import {Form as FormPrimitive} from "@base-ui/react/form"
import {clsx} from "clsx"
import styles from "./Form.module.css"

/**
 * A native `<form>` with Base UI's consolidated validation and error handling.
 * Collects the validity of every `Field` inside it, focuses the first invalid
 * control on submit, and accepts externally-supplied `errors` keyed by field
 * `name` (server/action errors).
 *
 * No form-state library is required, and one can be layered on later without
 * changing this primitive:
 * - **Native**: use `onFormSubmit(values)` (values keyed by each `Field`'s
 *   `name`) or `onSubmit(event)` + `new FormData(event.currentTarget)`.
 * - **React Hook Form / TanStack Form**: drive each `Field` via its
 *   `invalid`/`touched`/`dirty` props and submit with the library's own
 *   handler; pass server errors through `errors` (+ `onClearErrors`).
 *
 * `orientation="horizontal"` opts into a responsive label-beside-control layout
 * (like Bootstrap's `.form-horizontal`), but driven by the form's own width via a
 * container query: labels sit on the left when the form is wide and fall back to
 * stacked when it's narrow — no viewport media query, no JS.
 */
function Form<FormValues extends Record<string, unknown> = Record<string, unknown>>({
																						className,
																						orientation = "stacked",
																						...props
																					}: FormPrimitive.Props<FormValues> & {
	orientation?: "stacked" | "horizontal"
}) {
	return (
		<FormPrimitive
			data-slot="form"
			data-orientation={orientation}
			className={clsx(orientation === "horizontal" && styles.horizontal, className)}
			{...props}
		/>
	)
}

export {Form}
